// ─── TICKER NEWS PANEL — reusable per-symbol news feed ────────────────────────
// Dependencies: core (C, s, DataStatus), NewsService (newsService singleton)
// Drops into any tab that has a selected symbol — Charts, Markets, etc.
// Fetches on mount and whenever the ticker changes. Fails soft: no keys → a
// small informational prompt with registration links (not an error).

import { useState, useEffect, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { C, s, DataStatus, backendFetch } from "./core";
import { newsService } from "./NewsService";

// ─── BACKEND NEWS FETCH ───────────────────────────────────────────────────────
// Routes through window.QUANT_SIGNAL_BACKEND when configured (adds auth +
// Content-Type). The direct URL is a dead fallback; the backend path is the
// real call. Backend returns a JSON array of pre-normalized article objects.
async function fetchNews(path) {
  const res = await backendFetch("https://newsdata.io", path);
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

// ─── SENTIMENT BADGE ──────────────────────────────────────────────────────────
function SentimentBadge({ sentiment }) {
  const map = {
    positive: { bg: C.greenBg,  color: C.green,   text: "● POSITIVE" },
    negative: { bg: C.redBg,    color: "#FF5252", text: "● NEGATIVE" },
    neutral:  { bg: C.panelAlt, color: C.textDim, text: "● NEUTRAL"  },
  };
  const st = map[sentiment] || map.neutral;
  return (
    <span style={{
      background: st.bg,
      color: st.color,
      fontSize: 11,
      fontWeight: 700,
      padding: "1px 6px",
      borderRadius: 2,
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}>
      {st.text}
    </span>
  );
}

// ─── ARTICLE CARD ─────────────────────────────────────────────────────────────
function ArticleCard({ a }) {
  return (
    <div style={{ borderBottom: `1px solid ${C.border}`, padding: "10px 0" }}>
      {/* Top row: source · age — sentiment right */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <div style={{ fontSize: 11, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <span style={{ color: C.textMid, fontWeight: 700 }}>{a.source}</span>
          <span style={{ color: C.textDim }}> · {a.ageLabel}</span>
        </div>
        <SentimentBadge sentiment={a.sentiment} />
      </div>

      {/* Headline — max 2 lines */}
      <div style={{
        color: C.text,
        fontWeight: 700,
        fontSize: 13,
        lineHeight: 1.4,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        marginBottom: 4,
      }}>
        {a.title}
      </div>

      {/* Bottom: summary · read more */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ color: C.textDim, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {a.summary}
        </span>
        {a.url && (
          <a
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: C.blue, fontSize: 11, whiteSpace: "nowrap", textDecoration: "none", flexShrink: 0 }}
          >
            Read more ↗
          </a>
        )}
      </div>
    </div>
  );
}

// ─── NO-KEYS NOTICE ───────────────────────────────────────────────────────────
function NewsSetupNotice() {
  return (
    <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.8 }}>
      Data unavailable.
    </div>
  );
}

// ─── TICKER NEWS PANEL ────────────────────────────────────────────────────────
export function TickerNewsPanel({ ticker, keys }) {
  const [articles, setArticles] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [status,   setStatus]   = useState("offline");

  const hasKeys = !!(window.QUANT_SIGNAL_BACKEND || keys?.newsdataKey || keys?.gnewsKey || keys?.currentsKey);

  const load = useCallback(async () => {
    if (!ticker || !hasKeys) { setArticles([]); return; }
    setLoading(true);
    const arts = await fetchNews(`/api/news/ticker/${ticker.toUpperCase()}`);
    setArticles(arts);
    setStatus(arts.length ? "live" : "offline");
    setLoading(false);
  }, [ticker, keys, hasKeys]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={s.panel}>
      <div style={s.panelH}>
        <span style={s.panelT}>TICKER NEWS — {ticker}</span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <DataStatus
            status={hasKeys ? status : "mock"}
            source={hasKeys ? "" : "no keys"}
          />
          {hasKeys && (
            <button style={{ ...s.btn(), padding: "3px 7px" }} onClick={load} title="Refresh">
              <RefreshCw size={10} />
            </button>
          )}
        </div>
      </div>

      <div style={s.panelB}>
        {!hasKeys ? (
          <NewsSetupNotice />
        ) : loading ? (
          <div style={{ color: C.textDim, fontSize: 13, padding: "6px 0" }}>FETCHING NEWS...</div>
        ) : articles.length === 0 ? (
          <div style={{ color: C.textDim, fontSize: 13, padding: "6px 0" }}>
            No recent news found for {ticker}.
          </div>
        ) : (
          articles.slice(0, 5).map((a) => <ArticleCard key={a.id} a={a} />)
        )}
      </div>
    </div>
  );
}
